/**
 * Smooth ease-out-cubic tween from the previous value to a new target.
 * Used by AmbientTicker so spot/Δ digits glide rather than snap.
 */

import { useEffect, useRef, useState } from 'react';

export function useTween(target: number | null, durationMs = 600): number | null {
  const [val, setVal] = useState<number | null>(target);
  const fromRef = useRef<number | null>(target);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (target == null) {
      setVal(null);
      fromRef.current = null;
      return;
    }
    if (fromRef.current == null) {
      // First non-null value: snap.
      fromRef.current = target;
      setVal(target);
      return;
    }
    if (target === fromRef.current) return;

    const from = fromRef.current;
    const start = performance.now();
    const step = (now: number) => {
      const u = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - u, 3);
      setVal(from + (target - from) * eased);
      if (u < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
      }
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs]);

  return val;
}
